import { useCallback, useEffect, useRef } from 'react';

import { pushViewPromotion } from '~/gtm/events/promotion';
import type { GA4PromotionItem } from '~/gtm/types';

interface UseViewPromotionOptions {
  /** Threshold for IntersectionObserver (0-1) */
  threshold?: number;
  /** Debounce time in ms before pushing the event */
  debounceMs?: number;
}

/**
 * Hook to track view_promotion events using IntersectionObserver
 * Returns a ref callback to attach to banner/promotion elements
 */
export function useViewPromotion({ threshold = 0.5, debounceMs = 500 }: UseViewPromotionOptions = {}) {
  // Track which promotions have been viewed in this pageview
  const viewedPromotionsRef = useRef<Set<string>>(new Set());
  // Pending items to push
  const pendingItemsRef = useRef<GA4PromotionItem[]>([]);
  // Debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Map of observed elements
  const observedElementsRef = useRef<Map<Element, GA4PromotionItem>>(new Map());
  // IntersectionObserver instance
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Flush pending items
  const flushPendingItems = useCallback(() => {
    if (pendingItemsRef.current.length === 0) return;

    pushViewPromotion(pendingItemsRef.current);
    pendingItemsRef.current = [];
  }, []);

  // Schedule flush with debounce
  const scheduleFlush = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(flushPendingItems, debounceMs);
  }, [flushPendingItems, debounceMs]);

  // Create observer
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const promotion = observedElementsRef.current.get(entry.target);
          if (!promotion) return;

          const promotionId = promotion.promotion_id || promotion.promotion_name;
          if (!promotionId) return;

          // Skip if already viewed
          if (viewedPromotionsRef.current.has(promotionId)) return;

          // Mark as viewed
          viewedPromotionsRef.current.add(promotionId);

          // Add to pending
          pendingItemsRef.current.push(promotion);

          // Schedule flush
          scheduleFlush();
        });
      },
      { threshold }
    );

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [threshold, scheduleFlush]);

  // Ref callback to attach to promotion elements
  const observePromotion = useCallback(
    (element: HTMLElement | null, promotion: GA4PromotionItem): (() => void) | undefined => {
      if (!element || !observerRef.current) return undefined;

      // Store promotion data for this element
      observedElementsRef.current.set(element, promotion);

      // Start observing
      observerRef.current.observe(element);

      // Return cleanup function
      return () => {
        observerRef.current?.unobserve(element);
        observedElementsRef.current.delete(element);
      };
    },
    []
  );

  // Reset viewed promotions (call when navigating away)
  const resetViewed = useCallback(() => {
    viewedPromotionsRef.current.clear();
  }, []);

  return { observePromotion, resetViewed };
}
